import type { StreamStats, ConnectionState } from "../types";

// ─── Types ───────────────────────────────────────────────────────────

/** Quality level derived from stats. */
type QualityLevel = "good" | "moderate" | "poor";

/** Props for the StatsDisplay component. */
export interface StatsDisplayProps {
  /** Live stream statistics from WebRTC. */
  stats: StreamStats;
  /** Current connection state. */
  connectionState: ConnectionState;
  /** Whether the panel is visible. */
  visible?: boolean;
}

// ─── Quality thresholds ──────────────────────────────────────────────

/** FPS thresholds for quality indicator. */
const FPS_GOOD = 24;
const FPS_MODERATE = 15;

/** RTT thresholds in milliseconds. */
const RTT_GOOD = 50;
const RTT_MODERATE = 150;

/** Colors for quality levels. */
const QUALITY_COLORS: Record<QualityLevel, string> = {
  good: "#22c55e",     // green
  moderate: "#eab308", // yellow
  poor: "#ef4444",     // red
};

const QUALITY_LABELS: Record<QualityLevel, string> = {
  good: "Good",
  moderate: "Fair",
  poor: "Poor",
};

// ─── Helpers ─────────────────────────────────────────────────────────

/** Derive quality level from current stats. */
function deriveQuality(stats: StreamStats): QualityLevel {
  const { fps, latency_ms } = stats;

  // If we have no data, assume poor
  if (fps === null && latency_ms === null) return "poor";

  // Check FPS
  if (fps !== null && fps < FPS_MODERATE) return "poor";

  // Check RTT
  if (latency_ms !== null && latency_ms > RTT_MODERATE) return "poor";

  // Moderate range
  if (fps !== null && fps < FPS_GOOD) return "moderate";
  if (latency_ms !== null && latency_ms > RTT_GOOD) return "moderate";

  return "good";
}

/** Format a numeric stat for display. */
function formatStat(
  value: number | null,
  unit: string,
  decimals: number = 0,
): string {
  if (value === null) return "—";
  return `${value.toFixed(decimals)} ${unit}`;
}

// ─── Component ───────────────────────────────────────────────────────

export default function StatsDisplay({
  stats,
  connectionState,
  visible = true,
}: StatsDisplayProps) {
  if (!visible) return null;

  const isConnected = connectionState === "connected";
  const isConnecting = connectionState === "connecting";
  const hasData = stats.fps !== null || stats.latency_ms !== null;
  const quality = deriveQuality(stats);
  const qualityColor = QUALITY_COLORS[quality];
  const qualityLabel = isConnected && !hasData ? "Starting" : QUALITY_LABELS[quality];

  return (
    <div
      role="status"
      aria-label="Stream statistics"
      aria-live="polite"
      style={styles.container}
    >
      {/* Quality indicator */}
      <div style={styles.qualityRow}>
        <span
          aria-label={`Connection quality: ${qualityLabel}`}
          style={{
            ...styles.qualityDot,
            backgroundColor: isConnected ? qualityColor : "#666",
          }}
        />
        <span style={styles.qualityLabel}>
          {isConnecting ? "Connecting" : isConnected ? qualityLabel : "Offline"}
        </span>
      </div>

      {/* Stats grid */}
      <div style={styles.grid}>
        <StatItem
          label="FPS"
          value={formatStat(stats.fps, "fps")}
          ariaLabel={`Frames per second: ${stats.fps ?? "unavailable"}`}
        />
        <StatItem
          label="Bitrate"
          value={formatStat(stats.bitrate_kbps, "kbps")}
          ariaLabel={`Bitrate: ${stats.bitrate_kbps ?? "unavailable"} kilobits per second`}
        />
        <StatItem
          label="RTT"
          value={formatStat(stats.latency_ms, "ms")}
          ariaLabel={`Round trip time: ${stats.latency_ms ?? "unavailable"} milliseconds`}
        />
        <StatItem
          label="Resolution"
          value={stats.resolution ?? "—"}
          ariaLabel={`Resolution: ${stats.resolution ?? "unavailable"}`}
        />
      </div>
    </div>
  );
}

// ─── StatItem sub-component ──────────────────────────────────────────

interface StatItemProps {
  label: string;
  value: string;
  ariaLabel: string;
}

function StatItem({ label, value, ariaLabel }: StatItemProps) {
  return (
    <div style={styles.statItem} aria-label={ariaLabel}>
      <span style={styles.statLabel}>{label}</span>
      <span style={styles.statValue}>{value}</span>
    </div>
  );
}

// ─── Inline styles ───────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "8px 12px",
    background: "rgba(26, 26, 46, 0.9)",
    borderRadius: "8px",
    fontFamily: "ui-monospace, 'Cascadia Code', 'Fira Code', monospace",
    fontSize: "12px",
    color: "#e0e0e0",
    minWidth: "180px",
    backdropFilter: "blur(4px)",
  },
  qualityRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    paddingBottom: "4px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
  },
  qualityDot: {
    display: "inline-block",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  qualityLabel: {
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "6px 12px",
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  statLabel: {
    fontSize: "10px",
    color: "#8888aa",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  statValue: {
    fontSize: "13px",
    fontWeight: 500,
    fontVariantNumeric: "tabular-nums",
  },
};
